/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * Emergent Auth callback. Detects #session_id=... in the URL hash, exchanges it
 * with the backend for an access_token + session_token, and routes to the dashboard.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Open-redirect defense: only same-origin, single-slash-prefixed paths
 * are considered safe post-auth destinations. This rejects:
 *   - Absolute URLs (http://evil.com, //evil.com)
 *   - Protocol-relative URLs
 *   - Backslash-bypass (\/evil.com in some browsers)
 *   - Non-path values (mailto:, javascript:, data:)
 */
function isSafeInternalPath(p) {
  if (!p || typeof p !== "string") return false;
  if (!p.startsWith("/")) return false;         // must be a rooted path
  if (p.startsWith("//") || p.startsWith("/\\")) return false; // protocol-relative
  // Reject any control chars or scheme-like content
  if (/[\r\n\t]/.test(p)) return false;
  return true;
}

export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");
  const [needsRole, setNeedsRole] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [farmName, setFarmName] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/login", { replace: true });
      return;
    }
    const sid = m[1];
    setSessionId(sid);
    // Pull `next` off the query string BEFORE anything mutates history.
    // We prefer URL params over sessionStorage because they survive the
    // full cross-origin OAuth round-trip on every browser (including
    // mobile in-app browsers where sessionStorage is unreliable).
    const params = new URLSearchParams(location.search || "");
    const rawNext = params.get("next") || "";
    // Open-redirect defense: only accept relative in-app paths. Reject
    // anything that looks like an absolute URL, protocol-relative URL,
    // or backslash-based bypass. If unsafe, fall through to role default.
    const urlNext = isSafeInternalPath(rawNext) ? rawNext : "";
    // First attempt as customer; if role mismatch needed user can re-route via dialog.
    exchange(sid, "customer", null, urlNext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exchange = async (sid, role, farm = null, urlNext = "") => {
    try {
      const payload = { session_id: sid, role };
      if (role === "vendor" && farm) payload.farm_name = farm;
      const { data } = await api.post("/auth/google/session", payload);
      if (data.access_token) localStorage.setItem("ff2d_token", data.access_token);
      await refresh();
      // Clear hash and route based on role — but honor a pending
      // `next` intent if the user was mid-flow (e.g. FOUNDING1 promo
      // redemption on /pricing). Priority:
      //   1. `?next=` on the callback URL (survives OAuth round-trip)
      //   2. sessionStorage.post_auth_next (legacy fallback)
      //   3. localStorage.post_auth_next (mobile-safe fallback)
      //   4. role-based default
      window.history.replaceState({}, "", window.location.pathname);
      const ssNext = sessionStorage.getItem("post_auth_next");
      const lsNext = localStorage.getItem("post_auth_next");
      if (ssNext) sessionStorage.removeItem("post_auth_next");
      if (lsNext) localStorage.removeItem("post_auth_next");
      // Same open-redirect guard on the storage fallbacks.
      const safeSs = isSafeInternalPath(ssNext) ? ssNext : "";
      const safeLs = isSafeInternalPath(lsNext) ? lsNext : "";
      const pendingNext = urlNext || safeSs || safeLs || "";
      const defaultDest =
        data.role === "admin" ? "/admin"
        : data.role === "vendor" ? "/vendor"
        : "/account";
      navigate(pendingNext || defaultDest, { replace: true });
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Authentication failed");
    }
  };

  if (error) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="auth-callback-error">
        <h1 className="font-display text-3xl">We couldn't sign you in.</h1>
        <p className="mt-3 text-[var(--ff-text-secondary)]">{error}</p>
        <button onClick={() => navigate("/login")} className="btn-primary mt-8">Back to sign in</button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="auth-callback">
      <div className="animate-pulse w-16 h-16 rounded-full bg-[var(--ff-muted)] mx-auto" />
      <p className="mt-6 text-[var(--ff-text-secondary)]">Signing you in with Google…</p>
    </div>
  );
}
